// ============================================
// COURRIER ÉCARTÉ — DONNÉES ET LIBELLÉS
// ============================================
// Demande de Nathan du 14/10/2026 : chaque e-mail entrant est trié AVANT
// d'ouvrir un dossier. Une notification automatique (règles : adresse
// noreply, notice, en-têtes d'envoi de masse…) ou un e-mail que l'IA juge
// hors SAV avec assez d'assurance ne crée pas de dossier : il est tracé
// (InboundEmail IGNORE_NOTIFICATION ou ECARTE_IA) et listé ici. Le
// personnel peut toujours « Créer un dossier quand même » : rien n'est perdu.
//
// Routes (backend, inbound-mail/courrier-ecarte.ts) :
//   GET  /admin/courrier-ecarte?motif=&page=          liste paginée ({ data, meta })
//        motif : vide ou TRI (règle et IA), REGLE, IA, TOUS (tous les ignorés),
//        REPONSE_AUTOMATIQUE, SPAM, INTERNE, NON_REMIS ; 400 si inconnu.
//   POST /admin/courrier-ecarte/:id/creer-dossier     { ticketId, ticketRef }, idempotent
//
// La forme exacte de la page (tableau nu, { elements, total… } ou
// { data, …meta }) est lue avec tolérance : un champ absent ne casse pas
// l'écran, il ne s'affiche pas.

import { AdminApi } from '../../services/api';

/** Motif renvoyé par le serveur : règle, IA, ou autre statut IGNORE_*. */
export type MotifEcartement = 'REGLE' | 'IA' | string;

export interface CourrierEcarte {
  id: string;
  receivedAt: string | null;
  fromAddress: string;
  fromName: string | null;
  subject: string | null;
  motif: MotifEcartement;
  sousCategorie: string | null;
  /** Résumé de l'e-mail en une phrase (analyse IA). */
  resume: string | null;
  /** Assurance de l'IA, entre 0 et 1. */
  confiance: number | null;
  /** Raison donnée par la règle ou par l'IA. */
  raison: string | null;
  /** Boîte qui a reçu l'e-mail (libellé ou adresse). */
  boite: string | null;
  /** Début du message, pour juger sans ouvrir Outlook. */
  apercu: string | null;
  /** Dossier déjà créé depuis cet e-mail (« Créer un dossier quand même »). */
  ticketId: string | null;
  ticketRef: string | null;
}

export interface PageCourrierEcarte {
  elements: CourrierEcarte[];
  total: number;
  page: number;
  pages: number;
}

export interface DossierCreeDepuisCourrier {
  ticketId: string;
  ticketRef: string | null;
}

/** Filtre « motif » de la liste. Vide : le courrier écarté par le tri (règles et IA). */
export const MOTIFS_FILTRE: { value: string; label: string }[] = [
  { value: '', label: 'Écartés par le tri (règles et IA)' },
  { value: 'REGLE', label: 'Notification automatique' },
  { value: 'IA', label: 'Analyse IA' },
  { value: 'TOUS', label: 'Tous les e-mails ignorés' },
  { value: 'REPONSE_AUTOMATIQUE', label: 'Réponse automatique' },
  { value: 'SPAM', label: 'Spam évident' },
  { value: 'INTERNE', label: 'Expéditeur interne' },
  { value: 'NON_REMIS', label: 'Avis de non-remise' },
];

// --- Lecture tolérante -------------------------------------------------

type Brut = Record<string, unknown>;

const objet = (v: unknown): Brut | null => (typeof v === 'object' && v !== null && !Array.isArray(v) ? v as Brut : null);
const texte = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
const nombre = (v: unknown): number | null => {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

/** Ramène une confiance à [0, 1] : 0,92 et 92 donnent 0,92. Hors bornes : null. */
export function normaliserConfiance(v: unknown): number | null {
  const n = nombre(v);
  if (n === null || n < 0) return null;
  if (n <= 1) return n;
  if (n <= 100) return n / 100;
  return null;
}

function lireBoite(v: unknown): string | null {
  const o = objet(v);
  if (o) return texte(o.label) ?? texte(o.mailbox) ?? texte(o.adresse) ?? null;
  return texte(v);
}

/** Un élément de la liste, quelle que soit la façon dont le serveur nomme ses champs. */
export function lireCourrier(brut: unknown): CourrierEcarte | null {
  const e = objet(brut);
  if (!e) return null;
  const id = texte(e.id);
  if (!id) return null;
  // L'avis IA peut arriver à plat ou dans un objet (colonne JSON triIa).
  const ia = objet(e.triIa) ?? objet(e.analyseIa) ?? objet(e.ia) ?? {};
  const ticket = objet(e.ticket);
  return {
    id,
    receivedAt: texte(e.receivedAt) ?? texte(e.recuLe) ?? null,
    fromAddress: texte(e.fromAddress) ?? texte(e.expediteur) ?? '',
    fromName: texte(e.fromName),
    subject: texte(e.subject) ?? texte(e.objet),
    motif: texte(e.motif) ?? texte(e.status) ?? '',
    sousCategorie: texte(e.sousCategorie) ?? texte(ia.sousCategorie),
    resume: texte(e.resume) ?? texte(ia.resume),
    confiance: normaliserConfiance(e.confiance ?? ia.confiance),
    raison: texte(e.raison) ?? texte(ia.raison) ?? texte(e.statusReason),
    boite: texte(e.boiteLibelle) ?? lireBoite(e.boite ?? e.connecteur ?? e.connector),
    apercu: texte(e.apercu) ?? texte(e.bodyPreview),
    ticketId: texte(e.ticketId) ?? texte(ticket?.id),
    ticketRef: texte(e.ticketRef) ?? texte(ticket?.ticketRef),
  };
}

/** Page de la liste : tableau nu, { elements | emails | items | data, total, page, pages }. */
export function lirePageCourrier(brut: unknown, pageDemandee = 1): PageCourrierEcarte {
  const o = objet(brut);
  const liste = Array.isArray(brut)
    ? brut
    : ([o?.elements, o?.emails, o?.courriers, o?.items, o?.data].find(Array.isArray) as unknown[] | undefined) ?? [];
  const elements = liste.map(lireCourrier).filter((e): e is CourrierEcarte => e !== null);
  const total = nombre(o?.total) ?? nombre(o?.totalCount) ?? elements.length;
  const page = nombre(o?.page) ?? pageDemandee;
  const parPage = nombre(o?.limit) ?? nombre(o?.limite) ?? nombre(o?.parPage);
  const pages = nombre(o?.pages) ?? nombre(o?.totalPages)
    ?? (parPage && parPage > 0 ? Math.max(1, Math.ceil(total / parPage)) : 1);
  return { elements, total: Math.max(total, elements.length), page, pages: Math.max(1, pages) };
}

/** Réponse de « Créer un dossier quand même » : { ticketId, ticketRef } (ou { id, ticketRef }). */
export function lireDossierCree(brut: unknown): DossierCreeDepuisCourrier | null {
  const o = objet(brut);
  if (!o) return null;
  const ticket = objet(o.ticket);
  const ticketId = texte(o.ticketId) ?? texte(ticket?.id) ?? texte(o.id);
  if (!ticketId) return null;
  return { ticketId, ticketRef: texte(o.ticketRef) ?? texte(ticket?.ticketRef) ?? null };
}

// --- Libellés ----------------------------------------------------------

const SOUS_CATEGORIES: Record<string, string> = {
  prospection: 'prospection',
  fournisseur: 'fournisseur',
  candidature: 'candidature',
  notification: 'notification',
  publicite: 'publicité',
  facture: 'facture ou administratif',
  administratif: 'facture ou administratif',
  'facture/administratif': 'facture ou administratif',
  autre: 'autre',
};

const sansAccents = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

/** Sous-catégorie lisible (« publicité », « facture ou administratif »…). */
export function sousCategorieLisible(v: string | null | undefined): string | null {
  if (!v) return null;
  const cle = sansAccents(v).replace(/[_\s]+/g, '_');
  return SOUS_CATEGORIES[cle] ?? SOUS_CATEGORIES[cle.replace(/_/g, '/')] ?? v.trim().toLowerCase().replace(/_/g, ' ');
}

/** Autres motifs possibles (statuts IGNORE_* ou équivalents). */
const AUTRES_MOTIFS: Record<string, string> = {
  IGNORE_NOTIFICATION: 'Notification automatique',
  NOTIFICATION: 'Notification automatique',
  ECARTE_IA: 'Analyse IA',
  IGNORE_AUTO_REPLY: 'Réponse automatique',
  AUTO_REPLY: 'Réponse automatique',
  REPONSE_AUTOMATIQUE: 'Réponse automatique',
  IGNORE_SPAM: 'Spam évident',
  SPAM: 'Spam évident',
  IGNORE_INTERNE: 'Expéditeur interne',
  INTERNE: 'Expéditeur interne',
  IGNORE_NON_REMIS: 'Avis de non-remise',
  NON_REMIS: 'Avis de non-remise',
};

/** Motif en clair : « Notification automatique », « Analyse IA : prospection »… */
export function motifLisible(c: Pick<CourrierEcarte, 'motif' | 'sousCategorie'>): string {
  const motif = (c.motif || '').toUpperCase();
  const sous = sousCategorieLisible(c.sousCategorie);
  if (motif === 'REGLE') return 'Notification automatique';
  if (motif === 'IA' || motif === 'ECARTE_IA') {
    // Sans sous-catégorie, le serveur renvoie son libellé (« Écarté par l'IA ») : rien à répéter.
    return sous && !sansAccents(sous).startsWith('ecarte') && sous !== 'hors_sav' && sous !== 'hors sav'
      ? `Analyse IA : ${sous}` : 'Analyse IA : hors SAV';
  }
  return AUTRES_MOTIFS[motif] ?? (c.motif ? c.motif.toLowerCase().replace(/_/g, ' ') : 'Motif non précisé');
}

/** Couleur du badge : l'IA en bleu (avis à relire), les règles en neutre. */
export function classeMotif(motif: string): string {
  const m = (motif || '').toUpperCase();
  return m === 'IA' || m === 'ECARTE_IA'
    ? 'bg-sapin-100 text-sapin-800'
    : 'bg-brume text-graphite';
}

/** « sûr à 92 % » ; rien quand l'IA n'a pas donné de confiance. */
export function confianceLisible(confiance: number | null | undefined): string | null {
  const c = normaliserConfiance(confiance);
  if (c === null) return null;
  return `sûr à ${Math.round(c * 100)} %`;
}

/** Expéditeur lisible : « Nom <adresse> » ou l'adresse seule. */
export function expediteurLisible(c: Pick<CourrierEcarte, 'fromName' | 'fromAddress'>): string {
  if (c.fromName && c.fromName !== c.fromAddress) return `${c.fromName} <${c.fromAddress}>`;
  return c.fromAddress || 'Expéditeur inconnu';
}

/** Texte de la confirmation avant « Créer un dossier quand même ». */
export function texteConfirmationCreation(c: Pick<CourrierEcarte, 'fromAddress' | 'subject'>): string {
  return `Un dossier « à qualifier » sera ouvert à partir de l’e-mail de ${c.fromAddress || 'cet expéditeur'}`
    + `${c.subject ? ` (« ${c.subject} »)` : ''}. Aucun message ne part vers l’expéditeur : `
    + 'vous le qualifiez ensuite depuis la fiche du dossier.';
}

/** Nombre lisible pour le menu (99+ au-delà). */
export function compteurLisible(n: number | null | undefined): string | null {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return null;
  return n > 99 ? '99+' : String(n);
}

// --- Appels ------------------------------------------------------------

/** Lève l'erreur de l'API ; un appel qui lève avant la promesse est rattrapé pareil. */
export async function chargerCourrierEcarte(options: { motif?: string; page?: number } = {}): Promise<PageCourrierEcarte> {
  const page = options.page && options.page > 0 ? options.page : 1;
  const brut = await AdminApi.getCourrierEcarte({ motif: options.motif || undefined, page });
  return lirePageCourrier(brut, page);
}

export async function creerDossierDepuisCourrier(id: string): Promise<DossierCreeDepuisCourrier> {
  const brut = await AdminApi.creerDossierDepuisCourrier(id);
  const r = lireDossierCree(brut);
  if (!r) throw new Error('Le dossier a été demandé, mais le serveur n’a pas renvoyé sa référence. Rechargez la liste.');
  return r;
}

/** Événement émis après une création : le compteur du menu se relit. */
export const EVENEMENT_COURRIER_MODIFIE = 'kly:courrier-ecarte-modifie';

export function signalerCourrierModifie(): void {
  try { window.dispatchEvent(new Event(EVENEMENT_COURRIER_MODIFIE)); } catch { /* hors navigateur */ }
}
